import {
  enablePatches,
  produceWithPatches,
  setAutoFreeze,
  type Draft,
  type Patch,
} from "immer";
import * as Sentry from "@sentry/nextjs";
import type { Getter, Setter } from "jotai";
import type { Edge } from "@xyflow/react";
import type { NoxusReactflowNodeV2 } from "@/types/spotflow-v2";
import { edgesAtom, nodesAtom } from "../atoms";
import { historyStackAtom, isApplyingHistoryAtom, MAX_HISTORY } from "./atoms";
import type { AtomPatchPair, HistoryStack, Transaction } from "./types";

enablePatches();
// Defensive: ensure auto-freeze is on. Immer holds this in module-level
// global state; a previous in-memory toggle can survive hot-reload, so we
// re-assert on every module evaluation.
setAutoFreeze(true);

/**
 * Mutator passed to `recordTransaction`. Mutates immer drafts of the tracked
 * atoms in-place; returns nothing. The helper diffs the drafts against the
 * current state to produce patches.
 */
export interface TransactionDrafts {
  nodes: Draft<NoxusReactflowNodeV2[]>;
  edges: Draft<Edge[]>;
}

export interface RecordOptions {
  collapseKey?: string;
  /**
   * Time window inside which a transaction with the same `collapseKey` as the
   * stack top merges into it instead of creating a new entry. Defaults to
   * 1500ms (matches typing-burst expectations from VS Code/Figma).
   */
  collapseWindowMs?: number;
}

const DEFAULT_COLLAPSE_WINDOW_MS = 1500;

/**
 * Convert an immer patch path that starts with an array index into a path
 * that addresses the same entity by id, so the patch survives later
 * inserts/deletes in the array. The first segment (`0`, `1`, …) is replaced
 * with the entity's stable `id` from `state[index]`. Patches that don't start
 * with a numeric index (e.g., array-level add/remove) are returned untouched.
 */
function normalizePatchPath(
  patch: Patch,
  state: ReadonlyArray<{ id: string }>
): Patch {
  if (patch.path.length === 0) return patch;
  const head = patch.path[0];
  if (typeof head !== "number") return patch;

  const entity = state[head];
  if (!entity) return patch;

  return {
    ...patch,
    path: ["byId", entity.id, ...patch.path.slice(1)],
  };
}

function normalizePatches(
  patches: Patch[],
  state: ReadonlyArray<{ id: string }>
): Patch[] {
  return patches.map((p) => normalizePatchPath(p, state));
}

interface CombinedState {
  nodes: NoxusReactflowNodeV2[];
  edges: Edge[];
}

/**
 * Split combined-state patches whose path starts with `nodes` or `edges` into
 * per-atom patch arrays with the leading segment stripped. Patches that don't
 * start with one of those keys are dropped (defensive — should not happen
 * given the combined-state shape).
 */
function splitPatches(patches: Patch[], key: "nodes" | "edges"): Patch[] {
  const out: Patch[] = [];
  for (const p of patches) {
    if (p.path[0] !== key) continue;
    out.push({ ...p, path: p.path.slice(1) });
  }
  return out;
}

/**
 * Record a synchronous mutation against the tracked atoms (`nodesAtom`,
 * `edgesAtom`) as a single Transaction.
 *
 * Behavior:
 * - When `isApplyingHistoryAtom` is true (we're inside an undo/redo apply),
 *   the mutation runs *without* recording — required for the apply path
 *   itself, and as a re-entrancy guard for derived effects.
 * - One `produceWithPatches` call against a combined `{nodes, edges}` shape
 *   so cross-atom mutations don't hit immer's "object is not extensible"
 *   error from passing a frozen reference between atoms.
 * - Patches with array-index heads are rewritten to `byId/<id>/...` so they
 *   survive subsequent inserts/deletes.
 * - If `opts.collapseKey` matches the stack-top entry's key and the entry is
 *   younger than `collapseWindowMs`, the new patches replace the top's `redo`
 *   side while keeping its original `undo` (the baseline).
 * - Pushing always clears the redo stack and enforces `MAX_HISTORY`.
 *
 * History recording is **best-effort**. If anything throws (immer freeze,
 * patch produce, stack push, etc.) the helper falls back to applying the
 * mutation without recording and reports the error to Sentry. The editor
 * keeps working — the worst case is a missed undo entry.
 */
export function recordTransaction(
  get: Getter,
  set: Setter,
  label: string,
  mutate: (drafts: TransactionDrafts) => void,
  opts: RecordOptions = {}
): void {
  const isApplying = get(isApplyingHistoryAtom);
  const currentNodes = get(nodesAtom);
  const currentEdges = get(edgesAtom);

  let nextNodes: NoxusReactflowNodeV2[] =
    currentNodes as NoxusReactflowNodeV2[];
  let nextEdges: Edge[] = currentEdges as Edge[];
  let nodesPair: AtomPatchPair | undefined;
  let edgesPair: AtomPatchPair | undefined;
  let recordingFailed = false;

  try {
    const combined: CombinedState = {
      nodes: currentNodes as NoxusReactflowNodeV2[],
      edges: currentEdges as Edge[],
    };
    const [nextCombined, redoPatches, undoPatches] = produceWithPatches(
      combined,
      (draft) => {
        mutate({
          nodes: draft.nodes as Draft<NoxusReactflowNodeV2[]>,
          edges: draft.edges as Draft<Edge[]>,
        });
      }
    );

    nextNodes = nextCombined.nodes;
    nextEdges = nextCombined.edges;

    if (!isApplying) {
      const nodesChanged = nextNodes !== currentNodes;
      const edgesChanged = nextEdges !== currentEdges;
      if (nodesChanged) {
        nodesPair = {
          redo: normalizePatches(
            splitPatches(redoPatches, "nodes"),
            currentNodes
          ),
          undo: normalizePatches(splitPatches(undoPatches, "nodes"), nextNodes),
        };
      }
      if (edgesChanged) {
        edgesPair = {
          redo: normalizePatches(
            splitPatches(redoPatches, "edges"),
            currentEdges
          ),
          undo: normalizePatches(splitPatches(undoPatches, "edges"), nextEdges),
        };
      }
    }
  } catch (err) {
    recordingFailed = true;
    Sentry.captureException(err, {
      tags: { area: "editor-v2", component: "history" },
      extra: { label, phase: "record" },
    });
    // Fall back to applying the mutation without recording so the editor
    // keeps working. We rebuild shallow array copies and run the mutator
    // against them; the mutator typically only push/splice/re-assigns on
    // top-level fields, which works on plain (non-frozen) arrays.
    try {
      const fallbackNodes = currentNodes.map((n) => ({
        ...n,
      })) as NoxusReactflowNodeV2[];
      const fallbackEdges = currentEdges.map((e) => ({ ...e })) as Edge[];
      mutate({
        nodes: fallbackNodes as unknown as Draft<NoxusReactflowNodeV2[]>,
        edges: fallbackEdges as unknown as Draft<Edge[]>,
      });
      nextNodes = fallbackNodes;
      nextEdges = fallbackEdges;
    } catch (fallbackErr) {
      Sentry.captureException(fallbackErr, {
        tags: { area: "editor-v2", component: "history" },
        extra: { label, phase: "record-fallback" },
      });
      // Last resort: leave atoms as-is. The user's action is dropped, but
      // the editor doesn't crash.
      console.error("[history] mutation dropped:", label, fallbackErr);
      return;
    }
  }

  if (nextNodes !== currentNodes) set(nodesAtom, nextNodes);
  if (nextEdges !== currentEdges) set(edgesAtom, nextEdges);

  if (isApplying || recordingFailed) return;
  if (!nodesPair && !edgesPair) return;

  const incoming: Transaction = {
    label,
    nodes: nodesPair,
    edges: edgesPair,
    collapseKey: opts.collapseKey,
    timestamp: Date.now(),
  };

  try {
    set(historyStackAtom, (stack) => mergeOrPush(stack, incoming, opts));
  } catch (err) {
    Sentry.captureException(err, {
      tags: { area: "editor-v2", component: "history" },
      extra: { label, phase: "stack-push" },
    });
  }
}

function mergeOrPush(
  stack: HistoryStack,
  incoming: Transaction,
  opts: RecordOptions
): HistoryStack {
  const top = stack.undo.at(-1);
  const collapseWindow = opts.collapseWindowMs ?? DEFAULT_COLLAPSE_WINDOW_MS;
  const canCollapse =
    !!incoming.collapseKey &&
    !!top &&
    !top.sealed &&
    top.collapseKey === incoming.collapseKey &&
    incoming.timestamp - top.timestamp <= collapseWindow;

  if (canCollapse && top) {
    const merged: Transaction = {
      ...top,
      label: incoming.label,
      // Keep top.undo (the original baseline patches) so undoing the merged
      // entry rolls back to the state before the burst started.
      nodes: top.nodes
        ? { undo: top.nodes.undo, redo: incoming.nodes?.redo ?? top.nodes.redo }
        : incoming.nodes,
      edges: top.edges
        ? { undo: top.edges.undo, redo: incoming.edges?.redo ?? top.edges.redo }
        : incoming.edges,
      timestamp: incoming.timestamp,
    };
    return {
      undo: [...stack.undo.slice(0, -1), merged],
      redo: [],
    };
  }

  const truncated =
    stack.undo.length >= MAX_HISTORY
      ? stack.undo.slice(stack.undo.length - MAX_HISTORY + 1)
      : stack.undo;

  return { undo: [...truncated, incoming], redo: [] };
}

/**
 * Close any in-flight collapse window (e.g., user fired Cmd+Z, blurred a
 * field, switched nodes). Marks the top entry as `sealed` so the next
 * `recordTransaction` with a matching `collapseKey` won't merge into it.
 * No-op if the top entry has no collapse key or is already sealed.
 */
export function closeCollapseWindow(set: Setter, get: Getter): void {
  try {
    const stack = get(historyStackAtom);
    const top = stack.undo.at(-1);
    if (!top || !top.collapseKey || top.sealed) return;
    const sealed: Transaction = { ...top, sealed: true };
    set(historyStackAtom, {
      undo: [...stack.undo.slice(0, -1), sealed],
      redo: stack.redo,
    });
  } catch (err) {
    Sentry.captureException(err, {
      tags: { area: "editor-v2", component: "history" },
      extra: { phase: "close-collapse" },
    });
  }
}

/**
 * Helper for actions that should not be undoable but still need to mutate
 * tracked atoms (e.g., loading a workflow). Toggles isApplyingHistoryAtom
 * around the closure so any wrapped action atoms inside skip recording.
 */
export function withoutHistory<T>(get: Getter, set: Setter, fn: () => T): T {
  const wasApplying = get(isApplyingHistoryAtom);
  set(isApplyingHistoryAtom, true);
  try {
    return fn();
  } finally {
    set(isApplyingHistoryAtom, wasApplying);
  }
}
